import { ImpersonationAction } from 'store/api/users/types'

export type ActionChange = { label: string; value: string }

/* The assignment a save/submit action targeted, parsed from its path. */
export const parseAssignmentId = (path: string): number | null => {
  const match = path.match(/^\/v2\/assignments\/(\d+)(\/|$)/)
  return match ? Number(match[1]) : null
}

const PROFILE_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phoneNumber: 'Phone number',
  gender: 'Gender',
  nationality: 'Nationality',
  dateOfBirth: 'Date of birth',
  homeAddress: 'Home address',
  title: 'Title',
  website: 'Website',
}

/* Direct admin edits of a submitted answer (no impersonation session). Their payload holds the field
   title, old and new values, the optional reason and any sum recomputed from the new value. */
const ADMIN_EDIT_PATH = /^\/v2\/assignments\/(\d+)\/admin-edit\/\d+$/
type AdminEditValues = { title?: string; oldValues?: string[]; newValues?: string[] }
export const isAdminEdit = (action: ImpersonationAction) => action.sessionId === null
const adminEditTitle = (action: ImpersonationAction) => (action.payload as AdminEditValues | null)?.title

const joinValues = (values?: string[]) => (values || []).filter((v) => v !== '').join(', ') || '(empty)'
const adminEditChange = (label: string, change: AdminEditValues): ActionChange =>
  ({ label, value: `${joinValues(change.oldValues)} → ${joinValues(change.newValues)}` })

const prettifyKey = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()

/* Turn the sanitized change-shape into readable rows. The payload records only WHICH fields changed
   (never their values), so each row is a field label with a fixed "Changed" marker. resolveTitle maps
   a form element id to its title (only available where the call's form definition is on hand). */
const CHANGED = 'Changed'

export const getActionChanges = (
  action: ImpersonationAction,
  resolveTitle?: (elementId: number) => string | undefined,
): ActionChange[] => {
  const payload = action.payload as any
  if (!payload || typeof payload !== 'object') return []

  /* Admin edit: these do record the values, so each row is "old → new" */
  if (isAdminEdit(action)) {
    const sums = (payload.sums || []).map((sum: AdminEditValues) =>
      adminEditChange(`${sum.title || 'Sum'} (recalculated)`, sum))
    const reason = payload.reason ? [{ label: 'Reason', value: payload.reason }] : []
    return [adminEditChange(payload.title || 'Value', payload), ...sums, ...reason]
  }

  /* Application form save/submit: payload.data lists the element ids that were touched. */
  if (Array.isArray(payload.data)) {
    const changes: ActionChange[] = payload.data.map((entry: any) => {
      const id = Number(entry?.flowElementId)
      const label = (resolveTitle && id ? resolveTitle(id) : undefined) || (id ? `Element #${id}` : 'Element')
      return { label, value: CHANGED }
    })
    if (payload.newGroups) {
      changes.push({ label: 'Added group instances', value: String(payload.newGroups) })
    }
    return changes
  }

  /* Profile-style change: changedFields lists the field names that were touched. */
  if (Array.isArray(payload.changedFields)) {
    return payload.changedFields.map((key: string) => ({
      label: PROFILE_LABELS[key] || prettifyKey(key),
      value: CHANGED,
    }))
  }

  return []
}

export type ActionDescription = {
  /* Human sentence for the action; `ofApplication` marks entries that read "… of application <uid>". */
  label: string,
  ofApplication: boolean,
}

type Rule = {
  match: RegExp,
  method?: string,
  /* Receives the path match, so a label can name the assignment or application it touched */
  label: (m: RegExpMatchArray, action: ImpersonationAction) => string,
  /* Application-scoped: the entry gets the "of application <uid>" suffix and its changes are left to
     that application's own timeline. */
  ofApplication?: boolean,
}

/* Every audited endpoint as a sentence. First match wins, and the patterns are anchored, so order only
   matters where one path is a prefix of another (…/invite/admin before …/invite). Anything missing
   falls through to genericLabel — a raw endpoint is never put in front of a human. */
const RULES: Rule[] = [
  /* Assignments. Everything here belongs to an application, so the panel links to it. */
  { match: ADMIN_EDIT_PATH, label: (m, action) => `Admin edited ${adminEditTitle(action) || 'a value'} on assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/save/, label: (m) => `Saved changes on assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/submit/, label: (m) => `Submitted assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/upload\/\d+$/, method: 'DELETE', label: (m) => `Removed a file from assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/upload/, label: (m) => `Uploaded a file on assignment ${m[1]}`, ofApplication: true },
  /* Clears the answers the request names, not the assignment itself */
  { match: /^\/v2\/assignments\/(\d+)\/data$/, label: (m) => `Deleted answers on assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/deadline$/, label: (m) => `Changed the deadline of assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/visibility$/, label: (m) => `Changed the visibility of assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/revoke$/, label: (m) => `Revoked assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/coi\/\d+$/, label: (m) => `Updated the conflict of interest on an answer of assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/coi$/, label: (m) => `Updated the conflict of interest on assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/comment\/\d+$/, label: (m) => `Commented on an answer of assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/editable\/\d+$/, label: (m) => `Changed whether an answer of assignment ${m[1]} can be edited`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/review$/, label: (m) => `Reviewed assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/assignments\/(\d+)\/partition$/, label: (m) => `Changed the partition of assignment ${m[1]}`, ofApplication: true },
  /* One endpoint for both answers, and the payload holds which — so the label covers both */
  { match: /^\/v2\/assignments\/(\d+)\/accept$/, label: (m) => `Accepted or declined assignment ${m[1]}`, ofApplication: true },

  /* Applications */
  { match: /^\/v2\/applications$/, method: 'POST', label: () => 'Created a new application' },
  { match: /^\/v2\/applications\/duplicate\/\d+$/, label: () => 'Duplicated an application' },
  { match: /^\/v2\/applications\/unlock$/, label: () => 'Unlocked an application' },
  { match: /^\/v2\/applications\/\d+\/assign$/, label: () => 'Assigned a user to the application', ofApplication: true },
  { match: /^\/v2\/applications\/\d+\/assignments\/(\d+)\/lead$/, label: (m) => `Changed the lead reviewer of assignment ${m[1]}`, ofApplication: true },
  { match: /^\/v2\/applications\/\d+\/change_cutoff$/, label: () => 'Moved the application to another cut-off', ofApplication: true },
  { match: /^\/v2\/applications\/\d+\/owner$/, label: () => 'Changed the application owner', ofApplication: true },
  { match: /^\/v2\/applications\/\d+$/, method: 'DELETE', label: () => 'Deleted application', ofApplication: true },

  /* Account and profile */
  { match: /^\/users\/\d+$/, method: 'PATCH', label: () => 'Updated profile details' },
  { match: /^\/users\/email$/, label: () => 'Changed email address' },
  { match: /^\/users\/password$/, label: () => 'Changed password' },
  { match: /^\/users\/reset$/, label: () => 'Reset the password' },
  { match: /^\/users\/change-status$/, label: () => 'Changed a user\'s status' },
  { match: /^\/users$/, method: 'PUT', label: () => 'Created a user' },
  { match: /^\/users\/invite\/admin$/, label: () => 'Invited an admin' },
  { match: /^\/users\/invite$/, label: () => 'Invited a user' },
  { match: /^\/users\/revoke\/admin$/, label: () => 'Revoked an admin' },
  { match: /^\/users\/groups$/, method: 'POST', label: () => 'Created a user group' },
  { match: /^\/users\/groups\/\d+$/, method: 'PATCH', label: () => 'Updated a user group' },
  { match: /^\/users\/groups\/\d+$/, method: 'DELETE', label: () => 'Deleted a user group' },
  /* Read-only lookups. No longer recorded, but rows logged before that still need a sentence. */
  { match: /^\/users\/suggestions$/, label: () => 'Looked up user suggestions' },
  { match: /^\/users$/, method: 'POST', label: () => 'Looked up users' },
  { match: /^\/users\/logout$/, label: () => 'Logged out' },
  { match: /^\/users\/refresh$/, label: () => 'Refreshed the session' },

  /* Session boundaries. The start is written by the API when the token is minted, the exit by the
     user portal's Exit button, so every session reads as a pair. */
  { match: /^\/users\/impersonate\/exchange$/, label: () => 'Started impersonating' },
  { match: /^\/users\/impersonate\/exit$/, label: () => 'Stopped impersonating' },
]

const METHOD_VERB: Record<string, string> = {
  POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted',
}

/* Last resort, for an endpoint added after this list was written. Reads as a rough sentence rather
   than a route: DELETE /v2/assignments/12/data becomes "Deleted assignments data". */
const genericLabel = (method: string, path: string) => {
  const subject = path.split('/')
    .filter((part) => part && part !== 'v2' && !/^\d+$/.test(part))
    .map((part) => part.replace(/[-_]/g, ' ').replace(/([A-Z])/g, ' $1').toLowerCase())
    .join(' ')
    .trim()
  return `${METHOD_VERB[method] || 'Changed'} ${subject || 'a record'}`
}

export const describeAction = (action: ImpersonationAction): ActionDescription => {
  const { path, method } = action
  const rule = RULES.find((candidate) =>
    (!candidate.method || candidate.method === method) && candidate.match.test(path))
  if (!rule) return { label: genericLabel(method, path), ofApplication: false }

  return {
    label: rule.label(path.match(rule.match) as RegExpMatchArray, action),
    ofApplication: !!rule.ofApplication,
  }
}

/* Callers append "(Admin)" — the name alone would read as if the user acted themselves. */
export const adminName = (action: ImpersonationAction) =>
  `${action.adminFirstName || ''} ${action.adminLastName || ''}`.trim() || action.adminEmail || `admin #${action.adminId}`
